<template>
  <div class="page-content">
    <!-- Header -->
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Catégories</h1>
        <p class="page-subtitle">{{ rows.length }} catégorie{{ rows.length > 1 ? 's' : '' }}</p>
      </div>
      <v-btn color="primary" depressed @click="openNew">
        <v-icon left small>mdi-plus</v-icon>
        Nouvelle catégorie
      </v-btn>
    </div>

    <PreloaderComponent :enable="enable" />

    <!-- Search -->
    <div v-if="enable" class="d-flex justify-end mb-3">
      <input
        v-model="search"
        type="text"
        placeholder="Rechercher..."
        class="search-input"
      />
    </div>

    <!-- Data table -->
    <v-card flat v-if="enable" class="table-card">
      <v-data-table
        :headers="headers"
        :items="rows"
        :items-per-page="15"
        :search="search"
        no-data-text="Aucune catégorie trouvée"
        :footer-props="{ 'items-per-page-text': 'Par page', 'items-per-page-options': [10, 15, 25, -1] }"
      >
        <template v-slot:item.name="{ item }">
          <span class="font-weight-medium">{{ item.name }}</span>
        </template>
        <template v-slot:item.product_count="{ item }">
          <v-chip x-small outlined color="primary">{{ getProductCount(item.id) }}</v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="item.status === false ? 'error' : 'success'" dark>
            {{ item.status === false ? 'Inactif' : 'Actif' }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn icon small @click="edit(item)" title="Modifier">
            <v-icon small color="primary">mdi-pencil</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Create/Edit dialog -->
    <v-dialog v-model="dialog" max-width="440" @click:outside="close">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="primary" size="18" class="mr-2">{{ index > -1 ? 'mdi-pencil' : 'mdi-plus-circle' }}</v-icon>
          {{ form_title }}
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="dialog-body">
          <v-text-field
            v-model="category.name"
            label="Nom de la catégorie"
            outlined
            dense
            hide-details="auto"
            :rules="[v => !!v || 'Le nom est requis']"
            autofocus
            @keyup.enter="save"
            ref="nameInput"
            class="mb-3"
          ></v-text-field>
          <v-select
            v-model="category.status"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            label="Statut"
            outlined
            dense
            hide-details
          ></v-select>
          <v-switch
            v-if="barPrintingEnabled"
            v-model="category.is_bar_printing"
            label="Impression bar"
            dense
            hide-details
            class="mt-3"
          ></v-switch>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn text @click="close" class="mr-1">Annuler</v-btn>
          <v-btn
            color="primary"
            depressed
            :loading="loading"
            :disabled="loading || !category.name"
            @click="save"
          >
            {{ index > -1 ? 'Enregistrer' : 'Créer' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <FlashMessage :position="'right bottom'"></FlashMessage>
  </div>
</template>

<script>
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'
import PreloaderComponent from '../components/PreloaderComponent.vue'

export default {
  name: 'CategoriesView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    search: '',
    index: -1,
    loading: false,
    form_title: 'Nouvelle Catégorie',
    items: [{ text: 'Catégories', disabled: false, href: '/categories' }],
    enable: false,
    headers: [
      { text: 'NOM', align: 'start', sortable: true, value: 'name' },
      { text: 'PRODUITS', value: 'product_count', sortable: false, align: 'center', width: '100px' },
      { text: 'STATUT', value: 'status', sortable: true, align: 'center', width: '100px' },
      { text: '', value: 'actions', sortable: false, width: '60px', align: 'end' },
    ],
    rows: [],
    productCounts: {},
    dialog: false,
    category: { name: '', status: true, is_bar_printing: false },
    statusOptions: [
      { text: 'Actif', value: true },
      { text: 'Inactif', value: false },
    ],
  }),
  computed: {
    barPrintingEnabled() {
      try {
        var s = JSON.parse(localStorage.getItem('storeSettings') || '{}')
        return !!s.enable_bar_printing
      } catch (e) { return false }
    },
  },
  methods: {
    load() {
      var self = this
      this.$axios.get('/categories?orderasc=name')
        .then(function (response) {
          self.rows = response.data
          self.enable = true
          self.loadProductCounts()
        })
    },

    loadProductCounts() {
      var self = this
      this.$axios.get('/products')
        .then(function (response) {
          var counts = {}
          response.data.forEach(function (p) {
            if (p.category_id) {
              counts[p.category_id] = (counts[p.category_id] || 0) + 1
            }
          })
          self.productCounts = counts
        })
        .catch(function () {})
    },

    getProductCount(categoryId) {
      return this.productCounts[categoryId] || 0
    },

    openNew() {
      this.reset()
      this.dialog = true
      this.$nextTick(function () {
        if (this.$refs.nameInput) this.$refs.nameInput.focus()
      }.bind(this))
    },

    save() {
      if (!this.category.name || !this.category.name.trim()) return

      var self = this
      this.loading = true

      if (this.index > -1) {
        this.$axios.put('/categories/' + this.category.id + '?data=' + encodeURIComponent(JSON.stringify(self.category)))
          .then(function (response) {
            Object.assign(self.rows[self.index], response.data)
            self.dialog = false
            self.loading = false
            self.flashMessage.success({ title: 'Succès', message: 'Catégorie modifiée' })
            self.reset()
          })
          .catch(function () { self.loading = false })
      } else {
        this.$axios.post('/categories?data=' + encodeURIComponent(JSON.stringify(self.category)))
          .then(function (response) {
            self.rows.push(response.data)
            self.dialog = false
            self.loading = false
            self.flashMessage.success({ title: 'Succès', message: 'Catégorie créée' })
            self.reset()
          })
          .catch(function () { self.loading = false })
      }
    },

    edit(item) {
      this.index = this.rows.indexOf(item)
      this.category = Object.assign({}, item)
      this.form_title = 'Modifier : ' + this.category.name
      this.dialog = true
    },

    close() {
      this.dialog = false
      this.reset()
      this.loading = false
    },

    reset() {
      this.category = { name: '', status: true, is_bar_printing: false }
      this.index = -1
      this.form_title = 'Nouvelle Catégorie'
    },
  },
  mounted() { this.load() }
}
</script>

<style scoped>
.table-card {
  border: 1px solid #e8ecf1 !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.search-input {
  width: 260px;
  padding: 8px 12px;
  font-size: 0.8125rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  outline: none;
  color: #1a2332;
  background: #fff;
}

.search-input:focus {
  border-color: #194173;
  box-shadow: 0 0 0 2px rgba(25, 65, 115, 0.1);
}

.search-input::placeholder {
  color: #94a3b8;
}

.dialog-card {
  border-radius: 12px !important;
}

.dialog-title {
  font-size: 0.9375rem !important;
  font-weight: 600 !important;
  padding: 14px 20px 12px !important;
}

.dialog-body {
  padding: 16px 20px 12px !important;
}

.dialog-actions {
  padding: 8px 16px 12px !important;
}
</style>
